/* eslint-disable no-prototype-builtins */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumb, BackdropCircular} from 'components';
import {
  Card,
  Button,
  Tab,
  Tabs,
  Badge
} from '@material-ui/core';
import { Formik } from 'formik';
import {
  getAboutUsPageConfigsApi,
  saveAboutUsPageConfigsApi,
  getAssetManagerCategoryTagsApi
} from 'api';
import AboutUsGeneral from './AboutUsGeneral';
import AboutUsPageBanner from './AboutUsPageBanner';
import AboutUsPageWelcomeBlock from './AboutUsPageWelcomeBlock';
import AboutUsPageProviderBlock from './AboutUsPageProviderBlock';
import AboutUsPageCTAFull from './AboutUsPageCTAFull';
import AboutUsPageCTAContainer from './AboutUsPageCTAContainer';
import AboutUsPagePreview from './AboutUsPagePreview/AboutUsPagePreview';
import { validationSchema } from './validation';


const ctaTabs = [
  'General',
  'Page Top Banner',
  'Main Welcome Section',
  'Provider Section',
  'CTA Full Section',
  'CTA Container Section',
];

const AboutUsBlock = () => {
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);

  const {
    aboutUsPageConfigs,
    loadingAboutUsPageConfigs,
    aboutUsPageConfigsToUpdate,
    savingAboutUsPageConfigs
  } = useSelector((state: IReduxState) => state.store_configs);

  const {
    assetCategoryTags,
    loadingAssetCategoryTags
  } = useSelector((state: IReduxState) => state.asset_manager);

  const loading = loadingAboutUsPageConfigs
    || savingAboutUsPageConfigs
    || loadingAssetCategoryTags;

  const {
    general,
    page_top_banner,
    main_welcome_section,
    provider_section,
    cta_full_section,
    cta_container_section
  } = aboutUsPageConfigs;

  useEffect(() => {
    if (!Object.keys(aboutUsPageConfigs['general']).length) {
      getAboutUsPageConfigsApi({dispatch});
    }

    if (!assetCategoryTags.length) {
      getAssetManagerCategoryTagsApi({dispatch, categoryName: 'Storebuild-automaton'});
    }
  }, []);

  const handleTabChange = (e, value) => {
    setTabIndex(value);
  };

  const handleSave = () => {
    saveAboutUsPageConfigsApi({dispatch, configs: aboutUsPageConfigsToUpdate});
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setTabLabel = (tabName, errors: any) => {
    if (
      tabName === 'Provider Section' &&
      (errors?.provider_section?.hasOwnProperty('store_info/about_us_page/provider_image')
      || errors?.provider_section?.hasOwnProperty('store_info/about_us_page/provider_main_heading')
      || errors?.provider_section?.hasOwnProperty('store_info/about_us_page/provider_content'))
    ) {
      return (
        <Badge badgeContent={'!'} color="error">
          {tabName}
        </Badge>
      );
    }

    if (
      tabName === 'CTA Full Section' &&
      (errors?.cta_full_section?.hasOwnProperty('store_info/about_us_page/cta_full_background_image')
      || errors?.cta_full_section?.hasOwnProperty('store_info/about_us_page/cta_full_content'))
    ) {
      return (
        <Badge badgeContent={'!'} color="error">
          {tabName}
        </Badge>
      );
    }

    if (
      tabName === 'CTA Container Section' &&
      (errors?.cta_container_section?.hasOwnProperty('store_info/about_us_page/cta_container_front_image')
      || errors?.cta_container_section?.hasOwnProperty('store_info/about_us_page/cta_container_back_image')
      || errors?.cta_container_section?.hasOwnProperty('store_info/about_us_page/cta_container_content'))
    ) {
      return (
        <Badge badgeContent={'!'} color="error">
          {tabName}
        </Badge>
      );
    }

    return tabName;
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Breadcrumb
        routeSegments={[
          { name: 'Settings', path: '/settings#go_live_requirements' },
          { name: 'UI Configurations', path: '/settings#UI_configurations' },
          { name: 'About Us Page'}
        ]}
      />
      <div className="m-10">
        {!!Object.keys(aboutUsPageConfigs['general']).length &&
          <Formik
            onSubmit={() => handleSave()}
            initialValues={{
              general: {
                'store_info/about_us_page/is_about_us_page_enabled': general['store_info/about_us_page/is_about_us_page_enabled']
              },
              page_top_banner: {
                'store_info/about_us_page/is_top_banner_enabled': page_top_banner['store_info/about_us_page/is_top_banner_enabled'],
                'store_info/about_us_page/top_banner_image': page_top_banner['store_info/about_us_page/top_banner_image']
              },
              main_welcome_section: {
                'store_info/about_us_page/is_main_content_enabled': main_welcome_section['store_info/about_us_page/is_main_content_enabled'],
                'store_info/about_us_page/main_content_heading': main_welcome_section['store_info/about_us_page/main_content_heading'],
                'store_info/about_us_page/main_content_text': main_welcome_section['store_info/about_us_page/main_content_text']
              },
              provider_section: {
                'store_info/about_us_page/is_provider_section_enabled': provider_section['store_info/about_us_page/is_provider_section_enabled'],
                'store_info/about_us_page/provider_image': provider_section['store_info/about_us_page/provider_image'],
                'store_info/about_us_page/provider_main_heading': provider_section['store_info/about_us_page/provider_main_heading'],
                'store_info/about_us_page/provider_secondary_heading': provider_section['store_info/about_us_page/provider_secondary_heading'],
                'store_info/about_us_page/provider_content': provider_section['store_info/about_us_page/provider_content']
              },
              cta_full_section: {
                'store_info/about_us_page/is_cta_full_enabled': cta_full_section['store_info/about_us_page/is_cta_full_enabled'],
                'store_info/about_us_page/cta_full_background_image': cta_full_section['store_info/about_us_page/cta_full_background_image'],
                'store_info/about_us_page/cta_full_content': cta_full_section['store_info/about_us_page/cta_full_content']
              },
              cta_container_section: {
                'store_info/about_us_page/is_cta_container_enabled': cta_container_section['store_info/about_us_page/is_cta_container_enabled'],
                'store_info/about_us_page/cta_container_front_image': cta_container_section['store_info/about_us_page/cta_container_front_image'],
                'store_info/about_us_page/cta_container_back_image': cta_container_section['store_info/about_us_page/cta_container_back_image'],
                'store_info/about_us_page/cta_container_content': cta_container_section['store_info/about_us_page/cta_container_content']
              }
            }}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              setFieldValue,
              handleSubmit
            }) => (
              <form autoComplete="off" onSubmit={handleSubmit}>
                <Card className="p-8">
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    className="mb-8"
                  >
                    {ctaTabs.map((tab, idx) => (
                      <Tab
                        className="capitalize font-medium"
                        key={`tab-${idx}`}
                        value={idx}
                        label={setTabLabel(tab, errors)}
                      />
                    ))}
                  </Tabs>
                  {tabIndex === 0 && <AboutUsGeneral values={values} onChange={setFieldValue} />}
                  {tabIndex === 1 && <AboutUsPageBanner values={values} onChange={setFieldValue}  />}
                  {tabIndex === 2 && <AboutUsPageWelcomeBlock values={values} onChange={setFieldValue} />}
                  {tabIndex === 3 && <AboutUsPageProviderBlock values={values} errors={errors} onChange={setFieldValue} />}
                  {tabIndex === 4 && <AboutUsPageCTAFull values={values} errors={errors} onChange={setFieldValue} />}
                  {tabIndex === 5 && <AboutUsPageCTAContainer values={values} errors={errors} onChange={setFieldValue} />}
                </Card>
                <div className="flex justify-end">
                  <Button
                    variant="contained"
                    color="primary"
                    className="my-10"
                    type="submit"
                  >
                    Save Configurations
                  </Button>
                </div>
                <AboutUsPagePreview
                  values={values}
                />
              </form>
            )}
          </Formik>
        }
      </div>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default AboutUsBlock;
